import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, XMarkIcon, UsersIcon, PlayIcon, ChevronDownIcon, ChevronUpIcon, BookOpenIcon, LightBulbIcon, ChatBubbleLeftRightIcon, SparklesIcon, HandRaisedIcon } from '@heroicons/react/24/outline';
import { LivePlaysService, RatingsService, DefaultService } from '../generated-api';
import type { LivePlayOutput, LivePlayEvent, LivePlayUpdateInput, Player, LeagueOutput } from '../generated-api';
import { useData } from '../contexts/DataContext';
import { useSession } from '../contexts/SessionContext';
import PageHeader from '../components/common/PageHeader';
import PlayMetadataSection from '../components/PlayMetadataSection';
import SelectedGameCard from '../components/SelectedGameCard';
import LeagueTabNavigation from '../components/LeagueTabNavigation';
import LivePlayTimerCard from '../components/common/LivePlayTimerCard';
import LeagueTeamResults from '../components/LeagueTeamResults';
import DiscardGameModal from '../components/common/DiscardGameModal';
import type { FirstPlayerMinigameLocationState } from './FirstPlayerMinigamePage';
import type { FingerPickerLocationState } from './FingerPickerPage';
import { getAllMetadataFields } from '../constants/metadataFields';

const LivePlayPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { games, fetchGames, leagues, fetchLeagues } = useData();
  const { playerId } = useSession();

  const [livePlay, setLivePlay] = useState<LivePlayOutput | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [expectedDuration, setExpectedDuration] = useState<number | null>(null);
  const [leagueWinProbabilities, setLeagueWinProbabilities] = useState<Record<string, Record<string, number>>>({});
  const [leaguePlayerRatings, setLeaguePlayerRatings] = useState<Record<string, Record<string, { rating: number; sigma: number; gamesPlayed: number }>>>({});
  const [selectedLeagueTab, setSelectedLeagueTab] = useState<string>('');
  const [isParticipant, setIsParticipant] = useState(false);
  const [pendingMetadata, setPendingMetadata] = useState<Record<string, Record<string, any>>>({});
  const [lastSentMetadata, setLastSentMetadata] = useState<Record<string, Record<string, any>>>({});
  const [isPaused, setIsPaused] = useState(false);
  const [pausedElapsedSeconds, setPausedElapsedSeconds] = useState(0);
  const [totalPausedSeconds, setTotalPausedSeconds] = useState(0);
  const [pauseStartTime, setPauseStartTime] = useState<Date | null>(null);
  const [turnTimerDuration, setTurnTimerDuration] = useState<number>(60);
  const [isEventLogExpanded, setIsEventLogExpanded] = useState<boolean>(false);
  const [isTurnTimerExpanded, setIsTurnTimerExpanded] = useState<boolean>(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [gameSpecificLeagues, setGameSpecificLeagues] = useState<LeagueOutput[]>([]);

  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to get player name
  const getPlayerName = (playerId: string): string => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : `Player ${playerId.slice(-4)}`;
  };

  const minigameTeams = useMemo(() => {
    if (!livePlay) return [];
    return Object.entries(livePlay.teams).map(([teamName, playerIds]) => ({
      teamName,
      playerNames: playerIds.map(pid => getPlayerName(pid))
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePlay, players]);

  // Merge regular leagues with game-specific leagues
  const allLeagues = useMemo(() => {
    const gameLeagueIds = new Set(gameSpecificLeagues.map(l => l.id));
    return [...leagues.filter(l => !gameLeagueIds.has(l.id)), ...gameSpecificLeagues];
  }, [leagues, gameSpecificLeagues]);

  // Helper function to calculate total pause duration from event log
  const calculatePausedSecondsFromEvents = (eventLog: LivePlayEvent[]): { totalPaused: number; isPaused: boolean; pauseStart: Date | null } => {
    let totalPaused = 0;
    let pauseStart: Date | null = null;

    for (const event of eventLog) {
      if (event.eventType === 'pause') {
        pauseStart = new Date(event.timestamp);
      } else if (event.eventType === 'resume' && pauseStart !== null) {
        const resumeTime = new Date(event.timestamp);
        totalPaused += (resumeTime.getTime() - pauseStart.getTime()) / 1000;
        pauseStart = null;
      }
    }

    return { totalPaused, isPaused: pauseStart !== null, pauseStart };
  };

  useEffect(() => {
    if (id) {
      loadLivePlay();

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (livePlay && livePlay.startedAt && !isPaused) {
      updateElapsedTime();
      // Update timer every second
      timerIntervalRef.current = setInterval(() => {
        updateElapsedTime();
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    } else {
      // Clear interval if paused or no live play
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePlay, isPaused, totalPausedSeconds]);

  // Re-evaluate isParticipant when playerId or livePlay changes
  // (playerId may arrive after loadLivePlay finishes if auth is async)
  useEffect(() => {
    if (livePlay) {
      const allPlayerIds = Object.values(livePlay.teams).flat();
      setIsParticipant(playerId ? allPlayerIds.includes(playerId) : false);
    }
  }, [playerId, livePlay]);

  // Set default league tab when livePlay and leagues are both available
  useEffect(() => {
    if (livePlay && leagues.length > 0 && !selectedLeagueTab) {
      if (livePlay.leagueIds && livePlay.leagueIds.length > 0) {
        setSelectedLeagueTab(livePlay.leagueIds[0]);
      }
    }
  }, [livePlay, leagues, selectedLeagueTab]);

  const loadLivePlay = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [livePlayData, playersData] = await Promise.all([
        LivePlaysService.getLivePlayLivePlaysLivePlayIdGet({
          livePlayId: id,
        }),
        DefaultService.getPlayersPlayersGet(),
      ]);

      setLivePlay(livePlayData);
      setPlayers(playersData);

      // Initialize pause state from event log
      if (livePlayData.eventLog && livePlayData.eventLog.length > 0) {
        const pauseState = calculatePausedSecondsFromEvents(livePlayData.eventLog);
        setTotalPausedSeconds(pauseState.totalPaused);
        setIsPaused(pauseState.isPaused);
        setPauseStartTime(pauseState.pauseStart);
      }

      // Initialize last sent metadata with current state
      setLastSentMetadata(livePlayData.currentState || {});
      setPendingMetadata(livePlayData.currentState || {});

      // Load games, leagues, and game-specific leagues
      const loadPromises: Promise<any>[] = [];
      if (games.length === 0) {
        loadPromises.push(fetchGames());
      }
      if (leagues.length === 0) {
        loadPromises.push(fetchLeagues());
      }
      // Fetch game-specific leagues for this game
      loadPromises.push(
        DefaultService.getLeaguesLeaguesGet({ includeGameSpecific: true })
          .then((allLeagues) => {
            const gameLeagues = allLeagues.filter(
              l => l.isGameSpecificLeague && l.gameId === livePlayData.gameId
            );
            setGameSpecificLeagues(gameLeagues);
          })
          .catch((err) => {
            console.error('Failed to fetch game-specific leagues:', err);
          })
      );
      if (loadPromises.length > 0) {
        await Promise.all(loadPromises);
      }

      // Check if current user is a participant
      const allPlayerIds = Object.values(livePlayData.teams).flat();
      setIsParticipant(playerId ? allPlayerIds.includes(playerId) : false);

      // Set default selected league tab to first league
      if (livePlayData.leagueIds && livePlayData.leagueIds.length > 0) {
        setSelectedLeagueTab(livePlayData.leagueIds[0]);
      }

      // Load expected duration, win probabilities, and player ratings for all leagues
      await Promise.all([
        loadExpectedDuration(livePlayData),
        loadAllLeagueWinProbabilities(livePlayData),
        loadAllLeaguePlayerRatings(livePlayData),
      ]);
    } catch (err: any) {
      console.error('Error loading live play:', err);
      if (err.status === 404) {
        setError('Live play not found. It may have expired or been cancelled.');
      } else {
        setError('Failed to load live play. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  const loadExpectedDuration = async (livePlayData: LivePlayOutput) => {
    try {
      const totalPlayers = Object.values(livePlayData.teams).flat().length;
      const response = await DefaultService.getAverageDurationGamesGameIdAverageDurationGet({
        gameId: livePlayData.gameId,
        playerCount: totalPlayers,
      });
      setExpectedDuration(response.averageDurationMinutes || null);
    } catch (err) {
      console.error('Error loading expected duration:', err);
      // Don't show error, just leave expected duration as null
    }
  };

  const loadAllLeagueWinProbabilities = async (livePlayData: LivePlayOutput) => {
    const leagueProbabilities: Record<string, Record<string, number>> = {};

    // Build requests for all leagues
    const requests = (livePlayData.leagueIds || []).map(leagueId => ({
      leagueId,
      promise: RatingsService.calculateWinProbabilitiesWinProbabilitiesCalculatePost({
        requestBody: {
          teams: livePlayData.teams,
          leagueId,
        },
      }).catch(err => {
        console.warn(`Failed to load win probabilities for league ${leagueId}:`, err);
        return {};
      }),
    }));

    // Execute all requests in parallel
    const results = await Promise.all(requests.map(r => r.promise));

    // Process results
    results.forEach((probabilities, index) => {
      leagueProbabilities[requests[index].leagueId] = probabilities;
    });

    setLeagueWinProbabilities(leagueProbabilities);
  };

  const loadAllLeaguePlayerRatings = async (livePlayData: LivePlayOutput) => {
    const leagueRatings: Record<string, Record<string, { rating: number; sigma: number; gamesPlayed: number }>> = {};

    // Build requests for all leagues
    const requests = (livePlayData.leagueIds || []).map(leagueId => ({
      leagueId,
      promise: DefaultService.getLeaguePlayersLeaguesLeagueIdPlayersGet({
        leagueId,
      }).catch(err => {
        console.warn(`Failed to load player ratings for league ${leagueId}:`, err);
        return [];
      }),
    }));

    // Execute all requests in parallel
    const results = await Promise.all(requests.map(r => r.promise));

    // Process results - create map of playerId -> rating info
    results.forEach((leaguePlayers, index) => {
      const ratings: Record<string, { rating: number; sigma: number; gamesPlayed: number }> = {};
      leaguePlayers.forEach(player => {
        ratings[player.playerId] = {
          rating: player.rating,
          sigma: player.sigma,
          gamesPlayed: player.gamesPlayed
        };
      });
      leagueRatings[requests[index].leagueId] = ratings;
    });

    setLeaguePlayerRatings(leagueRatings);
  };

  const updateElapsedTime = () => {
    if (!livePlay || !livePlay.startedAt) return;

    const startedAt = new Date(livePlay.startedAt);
    const now = new Date();
    const diffMs = now.getTime() - startedAt.getTime();
    // Subtract total paused seconds from elapsed time
    const diffSeconds = Math.max(0, Math.floor(diffMs / 1000) - Math.floor(totalPausedSeconds));
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    // Store elapsed seconds for timeline
    setElapsedSeconds(diffSeconds);

    // Format time string
    if (diffHours > 0) {
      const remainingMinutes = diffMinutes % 60;
      const remainingSeconds = diffSeconds % 60;
      setElapsedTime(`${diffHours}h ${remainingMinutes}m ${remainingSeconds}s`);
    } else if (diffMinutes > 0) {
      const remainingSeconds = diffSeconds % 60;
      setElapsedTime(`${diffMinutes}m ${remainingSeconds}s`);
    } else {
      setElapsedTime(`${diffSeconds}s`);
    }
  };

  const handlePauseResume = async () => {
    if (!isParticipant) return;

    const now = new Date();
    const timestamp = now.toISOString();

    if (isPaused) {
      // Resume: calculate how long we were paused and add to total
      if (pauseStartTime) {
        const pauseDuration = (now.getTime() - pauseStartTime.getTime()) / 1000;
        setTotalPausedSeconds(prev => prev + pauseDuration);
      }
      setPauseStartTime(null);
      setIsPaused(false);

      // Send resume event to backend
      await handleAddEvent({
        timestamp,
        eventType: 'resume',
      });
    } else {
      // Pause: store current elapsed seconds and pause start time
      setIsPaused(true);
      setPausedElapsedSeconds(elapsedSeconds);
      setPauseStartTime(now);

      // Send pause event to backend
      await handleAddEvent({
        timestamp,
        eventType: 'pause',
      });
    }
  };

  const handleAddEvent = async (event: LivePlayEvent) => {
    if (!id || !isParticipant) return;

    try {
      const updateInput: LivePlayUpdateInput = { event };
      await LivePlaysService.updateLivePlayLivePlaysLivePlayIdPatch({
        livePlayId: id,
        requestBody: updateInput,
      });
    } catch (err: any) {
      console.error('Error adding event:', err);
      if (err.status === 403) {
        setError('Only participants can update a live play.');
      } else {
        setError('Failed to add event. Please try again.');
      }
    }
  };

  const sendPendingMetadataChanges = async (metadata: Record<string, Record<string, any>>) => {
    if (!id || !isParticipant) return;

    // Compare with last sent metadata to find differences
    const eventsToSend: LivePlayEvent[] = [];
    
    // Find new/changed values
    Object.entries(metadata).forEach(([teamName, fields]) => {
      Object.entries(fields).forEach(([fieldName, value]) => {
        const lastValue = lastSentMetadata?.[teamName]?.[fieldName];
        // Only send if value changed
        if (lastValue !== value) {
          eventsToSend.push({
            timestamp: new Date().toISOString(),
            eventType: 'metadata_update',
            teamName: teamName,
            fieldName: fieldName,
            value: value,
          });
        }
      });
    });

    // Find removed values
    Object.entries(lastSentMetadata).forEach(([teamName, fields]) => {
      Object.entries(fields).forEach(([fieldName, lastValue]) => {
        const currentValue = metadata?.[teamName]?.[fieldName];
        // If field was removed (was set, now undefined/null)
        if (lastValue !== undefined && lastValue !== null && (currentValue === undefined || currentValue === null)) {
          eventsToSend.push({
            timestamp: new Date().toISOString(),
            eventType: 'metadata_update',
            teamName: teamName,
            fieldName: fieldName,
            value: null,
          });
        }
      });
    });

    // Send all events
    if (eventsToSend.length > 0) {
      try {
        // Send events sequentially to avoid overwhelming the backend
        for (const event of eventsToSend) {
          await handleAddEvent(event);
        }
        // Update last sent metadata after successful send
        setLastSentMetadata(metadata);
        // Reload live play to get updated currentState
        await loadLivePlay();
      } catch (err) {
        console.error('Error sending pending metadata changes:', err);
      }
    }
  };

  const handleEndPlay = () => {
    // Navigate to NewPlayPage with fromLivePlay param to reuse Step 2
    navigate(`/plays/new?fromLivePlay=${id}`);
  };

  const handleOpenDiscardModal = () => {
    setShowDiscardModal(true);
  };

  const handleDiscardGame = async () => {
    if (!id) return;

    try {
      setIsDiscarding(true);
      await LivePlaysService.cancelLivePlayLivePlaysLivePlayIdDelete({
        livePlayId: id,
      });
      setShowDiscardModal(false);
      navigate('/');
    } catch (err: any) {
      console.error('Error discarding game:', err);
      if (err.status === 403) {
        setError('Only participants can discard a live play.');
      } else if (err.status === 404) {
        setError('Live play not found. It may have already been discarded.');
      } else {
        setError('Failed to discard game. Please try again.');
      }
      setShowDiscardModal(false);
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleStartTurnTimer = () => {
    if (id) {
      navigate(`/live-plays/${id}/turn-timer?duration=${turnTimerDuration}`);
    }
  };


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-body">Loading live play...</p>
        </div>
      </div>
    );
  }

  if (error && !livePlay) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-primary-700 hover:text-primary-800"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!livePlay) {
    return null;
  }

  const game = games.find(g => g.id === livePlay.gameId);

  // Transform livePlay teams to LeagueTeamResults format
  const getLeagueRelevantTeams = (leagueId: string): { teamName: string; playerIds: string[]; position: number }[] => {
    if (!livePlay) return [];

    // Get league members
    const league = allLeagues.find(l => l.id === leagueId);
    if (!league) return [];

    const leaguePlayerIds = new Set(league.playerIds);

    // Transform teams, filtering to only include league members
    const teamsWithPositions: { teamName: string; playerIds: string[]; position: number }[] = [];
    let position = 1;

    Object.entries(livePlay.teams).forEach(([teamName, playerIds]) => {
      // Filter to only include league members
      const leagueMemberIds = playerIds.filter(playerId => leaguePlayerIds.has(playerId));
      
      if (leagueMemberIds.length > 0) {
        teamsWithPositions.push({
          teamName,
          playerIds: leagueMemberIds,
          position: position++,
        });
      }
    });

    return teamsWithPositions;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader>
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/')}
            className="mr-4 p-2 text-disabled hover:text-body"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <ClockIcon className="h-6 w-6 text-primary-600" />
            <div>
              <h1 className="text-fluid-h1 font-bold text-heading">{livePlay.gameName}</h1>
              <p className="mt-2 text-body">Live Game in Progress</p>
            </div>
          </div>
        </div>
      </PageHeader>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex justify-between items-center">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Participant-only sections - Merged Game Card and Metadata */}
      {isParticipant && game && (
        <div className="bg-surface rounded-xl shadow-sm border border-edge p-6 mb-6">
          {/* Game Card */}
          {game && (
            <div className="mb-6 -mx-6 -mt-6">
              <div className="w-full">
                <SelectedGameCard
                  game={game}
                  gameExtensions={livePlay.gameExtensions || []}
                  onChangeGame={() => {}}
                  onToggleExtension={() => {}}
                  readOnly={true}
                />
              </div>
            </div>
          )}
          <PlayMetadataSection
            playDate={livePlay.startedAt}
            comment=""
            teams={Object.keys(livePlay.teams)}
            teamMetadata={pendingMetadata}
            onTeamMetadataChange={(metadata) => {
              // Update pending metadata immediately for UI responsiveness
              // Don't send API calls here - wait for user to click "Done"
              setPendingMetadata(metadata);
            }}
            onMetadataSave={(metadata) => {
              // Send changes to backend only when user clicks "Done"
              sendPendingMetadataChanges(metadata);
            }}
            readOnly={false}
            hideDateButton={true}
            onPlayDateChange={() => {}}
            onCommentChange={() => {}}
            playerMetadataFields={game.playerMetadataFields || []}
          />
        </div>
      )}

      {/* Timer Card */}
      <LivePlayTimerCard
        gameName={livePlay.gameName}
        elapsedTime={elapsedTime}
        elapsedSeconds={elapsedSeconds}
        expectedDurationMinutes={expectedDuration}
        isPaused={isPaused}
        pausedElapsedSeconds={pausedElapsedSeconds}
        isParticipant={isParticipant}
        onPauseResume={handlePauseResume}
        onFinish={handleEndPlay}
        onDiscard={handleOpenDiscardModal}
      />

      {/* Quick Actions: Rules, Strategy, Chat + Advanced toggle */}
      <div className="mb-6 grid grid-cols-4 gap-2">
        <button
          data-track="live_rules"
          onClick={() => navigate(`/games/${livePlay.gameId}/rules`)}
          className="flex flex-col items-center justify-center gap-1 p-3 bg-surface rounded-xl border border-edge hover:bg-surface-hover transition-colors"
        >
          <BookOpenIcon className="h-5 w-5 text-primary-600" />
          <span className="text-xs font-medium text-body">Rules</span>
        </button>
        <button
          data-track="live_strategy"
          onClick={() => navigate(`/games/${livePlay.gameId}/strategy`)}
          className="flex flex-col items-center justify-center gap-1 p-3 bg-surface rounded-xl border border-edge hover:bg-surface-hover transition-colors"
        >
          <LightBulbIcon className="h-5 w-5 text-yellow-600" />
          <span className="text-xs font-medium text-body">Strategy</span>
        </button>
        <button
          data-track="live_rules_chat"
          onClick={() => navigate(`/games/${livePlay.gameId}/chat`)}
          className="flex flex-col items-center justify-center gap-1 p-3 bg-surface rounded-xl border border-edge hover:bg-surface-hover transition-colors"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-600" />
          <span className="text-xs font-medium text-body">AI Chat</span>
        </button>
        <button
          data-track="live_first_player"
          onClick={() => navigate(`/live-plays/${id}/first-player`, { state: { teams: minigameTeams, livePlayId: id! } as FirstPlayerMinigameLocationState })}
          className="flex flex-col items-center justify-center gap-1 p-3 bg-surface rounded-xl border border-edge hover:bg-surface-hover transition-colors"
        >
          <SparklesIcon className="h-5 w-5 text-purple-600" />
          <span className="text-xs font-medium text-body">First Player</span>
        </button>
        <button
          data-track="live_finger_picker"
          onClick={() => navigate(`/live-plays/${id}/finger-picker`, { state: { teams: minigameTeams, livePlayId: id! } as FingerPickerLocationState })}
          className="flex flex-col items-center justify-center gap-1 p-3 bg-surface rounded-xl border border-edge hover:bg-surface-hover transition-colors"
        >
          <HandRaisedIcon className="h-5 w-5 text-sky-600" />
          <span className="text-xs font-medium text-body">Who starts?</span>
        </button>
        <button
          data-track="live_coin_toss"
          onClick={() => navigate(`/live-plays/${id}/coin-toss`)}
          className="flex flex-col items-center justify-center gap-1 p-3 bg-surface rounded-xl border border-edge hover:bg-surface-hover transition-colors"
        >
          <svg className="h-5 w-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v2M12 16v2M6 12h2M16 12h2" />
          </svg>
          <span className="text-xs font-medium text-body">Coin Toss</span>
        </button>
        <button
          onClick={() => setIsTurnTimerExpanded(!isTurnTimerExpanded)}
          className={`flex flex-col items-center justify-center gap-1 p-3 rounded-xl border transition-colors ${
            isTurnTimerExpanded
              ? 'bg-primary-50 border-primary-200 text-primary-700'
              : 'bg-surface border-edge hover:bg-surface-hover text-body'
          }`}
        >
          <ClockIcon className="h-5 w-5 text-primary-600" />
          <span className="text-xs font-medium">Turn Timer</span>
        </button>
      </div>

      {/* Advanced Controls (collapsible) */}
      {isTurnTimerExpanded && (
        <div className="mb-6 bg-gradient-to-br from-primary-50 via-primary-50 to-primary-100 rounded-2xl shadow-sm border border-primary-100 p-6">
          <h2 className="text-lg font-semibold text-heading mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-primary-600" />
            Turn Timer
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl px-4 py-2 border border-white shadow-sm">
              <select
                id="turn-timer-duration"
                value={turnTimerDuration}
                onChange={(e) => setTurnTimerDuration(Number(e.target.value))}
                className="bg-transparent text-2xl font-bold text-primary-700 focus:outline-none cursor-pointer appearance-none pr-2"
              >
                <option value={30}>30</option>
                <option value={60}>60</option>
                <option value={90}>90</option>
                <option value={120}>120</option>
                <option value={180}>180</option>
                <option value={300}>300</option>
              </select>
              <span className="text-sm font-medium text-muted">sec</span>
            </div>
            <button
              onClick={handleStartTurnTimer}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold shadow-lg shadow-primary-200 hover:shadow-xl hover:shadow-primary-300 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 active:scale-95"
            >
              <PlayIcon className="h-5 w-5" />
              Start Timer
            </button>
          </div>
        </div>
      )}

      {/* Teams */}
      <div className="mb-6">
        <div className="bg-surface shadow rounded-xl p-6">
          <h2 className="text-lg font-medium text-heading mb-6 flex items-center">
            <UsersIcon className="h-5 w-5 mr-2" />
            Teams
          </h2>

          {/* League Tab Navigation */}
          <LeagueTabNavigation
            key={`league-tabs-${allLeagues.length}`}
            leagues={allLeagues}
            playLeagueIds={livePlay.leagueIds || []}
            selectedLeagueId={selectedLeagueTab}
            onLeagueSelect={setSelectedLeagueTab}
          />

          {/* League-Specific Team Results */}
          {selectedLeagueTab && (
            <LeagueTeamResults
              leagueId={selectedLeagueTab}
              teams={getLeagueRelevantTeams(selectedLeagueTab)}
              players={players}
              ratingChanges={{}}
              ratingsBefore={{}}
              ratingsAfter={{}}
              winProbabilities={leagueWinProbabilities[selectedLeagueTab] || {}}
              getPlayerName={getPlayerName}
              teamMetadata={livePlay.currentState || undefined}
              metadataFields={getAllMetadataFields(game?.playerMetadataFields || [])}
              currentRatings={leaguePlayerRatings[selectedLeagueTab]}
            />
          )}
        </div>
      </div>

      {/* Participant-only sections */}
      {isParticipant && game && (
        <>
          {/* Event Log (Participants only) */}
          {livePlay.eventLog && livePlay.eventLog.length > 0 && (
            <div className="bg-surface rounded-xl shadow-sm border border-edge mb-6 overflow-hidden">
              <button
                onClick={() => setIsEventLogExpanded(!isEventLogExpanded)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-hover transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <ClockIcon className="w-5 h-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="text-left">
                    <h2 className="text-fluid-h2 font-semibold text-heading">Event Log</h2>
                    <p className="text-sm text-muted">{livePlay.eventLog.length} event{livePlay.eventLog.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  {isEventLogExpanded ? (
                    <ChevronUpIcon className="w-5 h-5 text-disabled" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-disabled" />
                  )}
                </div>
              </button>
              {isEventLogExpanded && (
                <div className="px-6 pb-6 pt-2 border-t border-edge">
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {livePlay.eventLog.map((event, index) => (
                      <div key={index} className="text-sm text-body border-l-2 border-edge-input pl-3 py-1">
                        <span className="text-muted">
                          {new Date(event.timestamp).toLocaleTimeString()}
                        </span>
                        {' - '}
                        {event.eventType === 'note' && (
                          <span>Note: {event.note}</span>
                        )}
                        {event.eventType === 'intermediate_score' && (
                          <span>
                            Score: {event.teamName}: {String(event.value)}
                          </span>
                        )}
                        {event.eventType === 'metadata_update' && (
                          <span>
                            {event.teamName}: {event.fieldName} = {String(event.value)}
                          </span>
                        )}
                        {event.eventType === 'pause' && (
                          <span className="text-yellow-700">Timer paused</span>
                        )}
                        {event.eventType === 'resume' && (
                          <span className="text-green-700">Timer resumed</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Discard Game Modal */}
      <DiscardGameModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={handleDiscardGame}
        isDiscarding={isDiscarding}
        gameName={livePlay.gameName}
      />

    </div>
  );
};

export default LivePlayPage;
